import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FaTelegramPlane } from "react-icons/fa";
import { SiTiktok } from "react-icons/si";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";

export default function GuruPassPending() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    const verifyAccess = async () => {
      const loginid = localStorage.getItem("currentDerivLogin");

      if (!loginid) {
        navigate("/login", { replace: true });
        return;
      }

      try {
        const q = query(
          collection(db, "deriv_users"),
          where("loginid", "==", loginid)
        );
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          localStorage.removeItem("currentDerivLogin");
          navigate("/login", { replace: true });
          return;
        }

        const data = snapshot.docs[0].data();

        if (data.approved === true) {
          navigate("/user", { replace: true });
          return;
        }

        setAuthorized(true);
      } catch {
        localStorage.removeItem("currentDerivLogin");
        navigate("/login", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    verifyAccess();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center text-cyan-400">
        <div className="flex flex-col items-center gap-6">
          <div className="w-14 h-14 rounded-full border-4 border-cyan-900/40 border-t-cyan-400 animate-spin"></div>
          <p className="text-lg tracking-wide">Consulting the Guru...</p>
        </div>
      </div>
    );
  }

  if (!authorized) return null;

  return (
    <div className="min-h-screen bg-[#050816] text-white relative overflow-hidden">

      {/* Background Glow */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,#06b6d4_0%,transparent_60%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_70%,#0891b2_0%,transparent_60%)]"></div>
      </div>

      <div className="relative max-w-6xl mx-auto px-6 py-16">

        {/* Header */}
        <div className="flex justify-between items-center mb-16">
          <span className="text-2xl font-black bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
            MatchesGuru
          </span>
          <Link to="/" className="text-cyan-400 hover:text-cyan-300 text-sm">
            ← Home
          </Link>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* LEFT SIDE */}
          <div>
            <h1 className="text-4xl md:text-5xl font-black leading-tight mb-6">
              Guru Pass Activation
            </h1>

            <p className="text-gray-300 text-lg mb-10 max-w-lg">
              Your Deriv account is linked. One final step unlocks full MatchesGuru precision.
              Complete the Guru Pass payment below and enter the inner circle.
            </p>

            {/* Benefits */}
            <div className="space-y-5">
              {[
                "High-probability Last Digit Matches signals",
                "All Volatility Indices supported",
                "Live precision timing engine",
                "Confidence scoring system",
                "Lifetime access + updates",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 bg-[#0b1225]/70 border border-cyan-900/40 p-4 rounded-2xl"
                >
                  <div className="w-8 h-8 rounded-full bg-cyan-600 flex items-center justify-center font-bold">
                    ✓
                  </div>
                  <span className="text-gray-200">{item}</span>
                </div>
              ))}
            </div>

            {/* Community */}
            <div className="mt-12">
              <p className="text-cyan-300 font-semibold mb-4">
                Join the MatchesGuru Circle
              </p>

              <div className="flex gap-8">
                <a
                  href="https://t.me/+DZqYd1cQD4VjMWI0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-cyan-400 hover:text-cyan-300 transition"
                >
                  <FaTelegramPlane className="text-2xl" />
                  Telegram
                </a>

                <a
                  href="https://www.tiktok.com/@derivguru?_r=1&_t=ZS-948jHjpbKho"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-cyan-400 hover:text-cyan-300 transition"
                >
                  <SiTiktok className="text-2xl" />
                  TikTok
                </a>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE PAYMENT CARD */}
          <div className="bg-gradient-to-br from-[#0b1225] to-[#060a18] border border-cyan-800/40 rounded-3xl p-10 shadow-2xl shadow-cyan-900/30">

            <div className="text-center mb-10">
              <p className="text-6xl font-black text-cyan-400">$150</p>
              <p className="text-gray-400 mt-2">One-Time Guru Pass</p>
            </div>

            <div className="bg-[#030712]/70 p-5 rounded-2xl border border-cyan-900/40 font-mono text-sm text-cyan-300 break-all text-center mb-6">
              TQhATfnViNAVqpcGXJfeGcNbMUJQ2uckr8
            </div>

            <p className="text-center text-gray-300 mb-8 text-sm">
              Send USDT (TRC20). After transfer, join Telegram to confirm.
            </p>

            <a
              href="https://t.me/+DZqYd1cQD4VjMWI0"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-gradient-to-r from-cyan-600 to-blue-500 hover:from-cyan-500 hover:to-blue-400 transition py-4 rounded-full font-bold text-center shadow-lg shadow-cyan-900/40"
            >
              I Sent Payment – Enter Community
            </a>

            <p className="text-xs text-gray-500 text-center mt-6">
              Activation typically completed shortly after confirmation.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-20 text-center text-gray-500 text-sm border-t border-cyan-950/40 pt-8">
          © 2026 MatchesGuru • Trading involves risk
        </div>
      </div>
    </div>
  );
}